import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';

const SponsorsContainer = styled.section`
  padding: 6rem 0;
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 3rem 0;
  }
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 20% 20%, rgba(0, 128, 214, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(255, 229, 0, 0.03) 0%, transparent 50%);
  }
`;

const SponsorsHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const SponsorsTitle = styled(motion.h2)`
  font-size: 3rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
  }
`;

const SponsorsSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 0.5rem;
  }
`;

const SponsorTier = styled.div`
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
  }
`;

const TierHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 1.5rem;
    padding: 0 1rem;
  }
`;

const TierTitle = styled.h3`
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${({ tier, theme }) => {
    switch(tier) {
      case 'diamond': return '#B9F2FF';
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'lanyard': return theme.colors.primary.purple;
      case 'ctf': return '#FF6B6B';
      case 'supporting': return theme.colors.primary.yellow;
      case 'community': return theme.colors.primary.blue;
      default: return theme.colors.text.primary;
    }
  }};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

// const TierIcon = styled.span`
//   font-size: 1.5rem;
//   margin-right: 0.5rem;
  
//   @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
//     font-size: 1.2rem;
//   }
// `;

const SponsorGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return '1fr';
      case 'gold': return 'repeat(auto-fit, minmax(400px, 1fr))';
      case 'silver': return 'repeat(auto-fit, minmax(350px, 1fr))';
      case 'lanyard': return 'repeat(auto-fit, minmax(300px, 1fr))';
      case 'ctf': return '1fr';
      case 'supporting': return 'repeat(auto-fit, minmax(280px, 1fr))';
      case 'community': return 'repeat(auto-fit, minmax(250px, 1fr))';
      default: return 'repeat(auto-fit, minmax(320px, 1fr))';
    }
  }};
  gap: 2.5rem;
  margin-bottom: 3rem;
  max-width: ${({ tier }) => tier === 'diamond' || tier === 'ctf' ? '600px' : 'none'};
  margin-left: ${({ tier }) => tier === 'diamond' || tier === 'ctf' ? 'auto' : '0'};
  margin-right: ${({ tier }) => tier === 'diamond' || tier === 'ctf' ? 'auto' : '0'};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
    max-width: none;
  }
`;

const SponsorCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ tier, theme }) => {
    switch(tier) {
      case 'diamond': return '#B9F2FF';
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'lanyard': return theme.colors.primary.purple;
      case 'ctf': return '#FF6B6B';
      case 'supporting': return theme.colors.primary.yellow;
      case 'community': return theme.colors.primary.blue;
      default: return theme.colors.border;
    }
  }};
  border-radius: 20px;
  padding: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return '3rem';
      case 'gold': return '2.8rem';
      case 'silver': return '2.5rem';
      case 'lanyard': return '2.3rem';
      case 'ctf': return '2.8rem';
      case 'supporting': return '2rem';
      case 'community': return '1.8rem';
      default: return '2.5rem';
    }
  }};
  transition: all 0.4s ease;
  backdrop-filter: blur(15px);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ tier, theme }) => {
      switch(tier) {
        case 'diamond': return 'linear-gradient(90deg, #B9F2FF, #87CEEB)';
        case 'gold': return 'linear-gradient(90deg, #FFD700, #FFA500)';
        case 'silver': return 'linear-gradient(90deg, #C0C0C0, #A0A0A0)';
        case 'lanyard': return theme.gradients.primary;
        case 'ctf': return 'linear-gradient(90deg, #FF6B6B, #FF4757)';
        case 'supporting': return theme.gradients.primary;
        case 'community': return 'linear-gradient(90deg, #0080D6, #0066CC)';
        default: return theme.gradients.primary;
      }
    }};
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100px;
    height: 100px;
    background: ${({ tier }) => {
      switch(tier) {
        case 'diamond': return 'radial-gradient(circle, rgba(185, 242, 255, 0.1) 0%, transparent 70%)';
        case 'gold': return 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)';
        case 'silver': return 'radial-gradient(circle, rgba(192, 192, 192, 0.1) 0%, transparent 70%)';
        case 'lanyard': return 'radial-gradient(circle, rgba(138, 43, 226, 0.1) 0%, transparent 70%)';
        case 'ctf': return 'radial-gradient(circle, rgba(255, 107, 107, 0.1) 0%, transparent 70%)';
        case 'supporting': return 'radial-gradient(circle, rgba(255, 229, 0, 0.1) 0%, transparent 70%)';
        case 'community': return 'radial-gradient(circle, rgba(0, 128, 214, 0.1) 0%, transparent 70%)';
        default: return 'radial-gradient(circle, rgba(255, 229, 0, 0.1) 0%, transparent 70%)';
      }
    }};
    border-radius: 50%;
    transition: all 0.4s ease;
    transform: scale(0);
  }
  
  &:hover {
    transform: translateY(-8px);
    border-color: ${({ tier, theme }) => {
      switch(tier) {
        case 'diamond': return '#87CEEB';
        case 'gold': return '#FFA500';
        case 'silver': return '#A0A0A0';
        case 'lanyard': return theme.colors.primary.purple;
        case 'ctf': return '#FF4757';
        case 'supporting': return theme.colors.primary.yellow;
        case 'community': return theme.colors.primary.blue;
        default: return theme.colors.primary.yellow;
      }
    }};
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.15),
      ${({ tier }) => {
        switch(tier) {
          case 'diamond': return '0 0 30px rgba(185, 242, 255, 0.2)';
          case 'gold': return '0 0 30px rgba(255, 215, 0, 0.2)';
          case 'silver': return '0 0 30px rgba(192, 192, 192, 0.2)';
          case 'lanyard': return '0 0 30px rgba(138, 43, 226, 0.2)';
          case 'ctf': return '0 0 30px rgba(255, 107, 107, 0.2)';
          case 'supporting': return '0 0 30px rgba(255, 229, 0, 0.1)';
          case 'community': return '0 0 30px rgba(0, 128, 214, 0.1)';
          default: return '0 0 30px rgba(255, 229, 0, 0.1)';
        }
      }};
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
  
  &:hover::after {
    transform: scale(3);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    border-radius: 15px;
    
    &:hover {
      transform: none;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }
  }
`;

const SponsorContent = styled.div`
  position: relative;
  z-index: 2;
`;

const SponsorLogoContainer = styled.div`
  width: 100%;
  height: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return '120px';
      case 'gold': return '110px';
      case 'silver': return '100px';
      case 'lanyard': return '100px';
      case 'ctf': return '110px';
      case 'supporting': return '95px';
      case 'community': return '90px';
      default: return '100px';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 1rem;
  transition: all 0.3s ease;
  
  ${SponsorCard}:hover & {
    background: rgba(255, 255, 255, 0.95);
    border-color: ${({ tier, theme }) => {
      switch(tier) {
        case 'diamond': return '#B9F2FF';
        case 'gold': return '#FFD700';
        case 'silver': return '#C0C0C0';
        case 'lanyard': return theme.colors.primary.purple;
        case 'ctf': return '#FF6B6B';
        case 'supporting': return theme.colors.primary.yellow;
        case 'community': return theme.colors.primary.blue;
        default: return theme.colors.primary.blue;
      }
    }};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 85px;
    margin-bottom: 1.5rem;
    padding: 0.75rem;
  }
`;

const SponsorLogo = styled.img`
  max-width: 100%;
  max-height: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return '80px';
      case 'gold': return '70px';
      case 'silver': return '65px';
      case 'lanyard': return '65px';
      case 'ctf': return '70px';
      case 'supporting': return '60px';
      case 'community': return '55px';
      default: return '60px';
    }
  }};
  width: auto;
  height: auto;
  object-fit: contain;
  filter: brightness(0.9);
  transition: all 0.3s ease;
  
  ${SponsorCard}:hover & {
    filter: brightness(1);
    transform: scale(1.05);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-height: 55px;
    
    ${SponsorCard}:hover & {
      transform: none;
    }
  }
`;

const SponsorFallbackLogo = styled.div`
  font-size: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return '3rem';
      case 'gold': return '2.8rem';
      case 'silver': return '2.6rem';
      case 'lanyard': return '2.6rem';
      case 'ctf': return '2.8rem';
      case 'supporting': return '2.4rem';
      case 'community': return '2.2rem';
      default: return '2.5rem';
    }
  }};
  color: ${({ tier, theme }) => {
    switch(tier) {
      case 'diamond': return '#B9F2FF';
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'lanyard': return theme.colors.primary.purple;
      case 'ctf': return '#FF6B6B';
      case 'supporting': return theme.colors.primary.yellow;
      case 'community': return theme.colors.primary.blue;
      default: return theme.colors.primary.yellow;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const SponsorInfo = styled.div`
  text-align: center;
`;

const SponsorName = styled.h4`
  font-size: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return '1.6rem';
      case 'gold': return '1.5rem';
      case 'silver': return '1.4rem';
      case 'lanyard': return '1.3rem';
      case 'ctf': return '1.5rem';
      case 'supporting': return '1.2rem';
      case 'community': return '1.1rem';
      default: return '1.4rem';
    }
  }};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
  font-weight: 700;
  line-height: 1.3;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
  }
`;

const SponsorDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const SponsorTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.4rem;
  }
`;

const SponsorTag = styled.span`
  background: ${({ tier }) => {
    switch(tier) {
      case 'diamond': return 'rgba(185, 242, 255, 0.1)';
      case 'gold': return 'rgba(255, 215, 0, 0.1)';
      case 'silver': return 'rgba(192, 192, 192, 0.1)';
      case 'lanyard': return 'rgba(138, 43, 226, 0.1)';
      case 'ctf': return 'rgba(255, 107, 107, 0.1)';
      case 'supporting': return 'rgba(255, 229, 0, 0.1)';
      case 'community': return 'rgba(0, 128, 214, 0.1)';
      default: return 'rgba(0, 128, 214, 0.1)';
    }
  }};
  color: ${({ tier, theme }) => {
    switch(tier) {
      case 'diamond': return '#B9F2FF';
      case 'gold': return '#FFD700';
      case 'silver': return '#C0C0C0';
      case 'lanyard': return theme.colors.primary.purple;
      case 'ctf': return '#FF6B6B';
      case 'supporting': return theme.colors.primary.yellow;
      case 'community': return theme.colors.primary.blue;
      default: return theme.colors.primary.blue;
    }
  }};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid ${({ tier }) => {
    switch(tier) {
      case 'diamond': return 'rgba(185, 242, 255, 0.2)';
      case 'gold': return 'rgba(255, 215, 0, 0.2)';
      case 'silver': return 'rgba(192, 192, 192, 0.2)';
      case 'lanyard': return 'rgba(138, 43, 226, 0.2)';
      case 'ctf': return 'rgba(255, 107, 107, 0.2)';
      case 'supporting': return 'rgba(255, 229, 0, 0.2)';
      case 'community': return 'rgba(0, 128, 214, 0.2)';
      default: return 'rgba(0, 128, 214, 0.2)';
    }
  }};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.25rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const BecomeSponsorSection = styled.div`
  background: ${({ theme }) => theme.gradients.card};
  border-radius: 25px;
  padding: 4rem 3rem;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(20px);
  margin-top: 5rem;
  position: relative;
  z-index: 2;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 128, 214, 0.05) 0%, rgba(255, 229, 0, 0.05) 100%);
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
    margin: 3rem 1rem 0 1rem;
  }
`;

const SponsorshipBenefits = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin: 2rem 0;
  }
`;

const BenefitCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${({ theme }) => theme.gradients.primary};
    border-radius: 2px 2px 0 0;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
  
  h4 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 1rem;
    font-size: 1.2rem;
    font-weight: 600;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.95rem;
    line-height: 1.6;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    
    &:hover {
      transform: none;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    }
    
    h4 {
      font-size: 1.1rem;
      margin-bottom: 0.75rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
  }
`;

const SponsorSectionTitle = styled.h3`
  font-size: 2.5rem;
  color: #FFE500;
  margin-bottom: 1.5rem;
  font-weight: 700;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const SponsorSectionDescription = styled.p`
  color: #B0C4DE;
  font-size: 1.2rem;
  margin-bottom: 3rem;
  max-width: 700px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: 2rem;
    padding: 0 0.5rem;
  }
`;

const Sponsors = () => {
  const [imageErrors, setImageErrors] = useState({});

  const sponsorTiers = [
    {
      tier: 'diamond',
      title: '💎 Diamond Sponsors',
      sponsors: [
        { 
          name: 'Trend Micro', 
          description: 'Global cybersecurity leader providing comprehensive security solutions for enterprises, including threat detection, response, and prevention technologies.',
          icon: '🛡️',
          logo: 'trend.png',
          tags: ['Enterprise Security', 'Threat Detection', 'Cloud Security']
        }
      ]
    },
    {
      tier: 'gold',
      title: '🥇 Gold Sponsors',
      sponsors: [
        { 
          name: 'Cobalt', 
          description: 'Pentesting as a Service (PtaaS) platform that combines manual penetration testing with an intuitive SaaS platform for continuous security testing.',
          icon: '🔒',
          logo: 'cobalt.png',
          tags: ['Penetration Testing', 'Security Platform', 'SaaS']
        }
      ]
    },
    {
      tier: 'silver',
      title: '🥈 Silver Sponsors',
      sponsors: [
        { 
          name: 'Real Time Solutions', 
          description: 'IT consulting and managed services provider specializing in cybersecurity, cloud solutions, and digital transformation for businesses.',
          icon: '☁️',
          logo: 'realtime.png',
          tags: ['IT Consulting', 'Managed Services', 'Cloud Solutions']
        },
        { 
          name: 'Plex Trac', 
          description: 'Cybersecurity platform that streamlines vulnerability management and security testing workflows for organizations of all sizes.',
          icon: '📊',
          logo: 'plaxtrec.png',
          tags: ['Vulnerability Management', 'Security Testing', 'Workflow Automation']
        }
      ]
    },
    {
      tier: 'lanyard',
      title: '🏷️ Lanyard Sponsors',
      sponsors: [
        { 
          name: 'Loial', 
          description: 'Identity and access management solutions provider focusing on secure authentication and authorization systems for enterprises.',
          icon: '🔐',
          logo: 'loial.png',
          tags: ['Identity Management', 'Access Control', 'Authentication']
        },
        { 
          name: 'HPE Aruba Networking', 
          description: 'Enterprise networking solutions with AI-powered insights, providing secure, intelligent edge-to-cloud networking infrastructure.',
          icon: '🌐',
          logo: 'hpe.png',
          tags: ['Networking', 'AI-Powered', 'Edge Computing', 'Enterprise']
        }
      ]
    },
    {
      tier: 'ctf',
      title: '🚩 CTF Sponsor',
      sponsors: [
        { 
          name: 'HackerOne', 
          description: 'Leading bug bounty and vulnerability disclosure platform connecting organizations with a global community of ethical hackers.',
          icon: '🐛',
          logo: 'hackerone.png',
          tags: ['Bug Bounty', 'Vulnerability Disclosure', 'Crowdsourced Security']
        }
      ]
    },
    {
      tier: 'supporting',
      title: '🤝 Supporting Sponsors',
      sponsors: [
        { 
          name: 'Black Hills Information Security', 
          description: 'Cybersecurity consulting and training company providing penetration testing, security assessments, and professional development.',
          icon: '⚔️',
          logo: 'blackhills.png',
          tags: ['Security Consulting', 'Training', 'Penetration Testing']
        },
        { 
          name: 'TEC|3|4', 
          description: 'Technology solutions provider specializing in cybersecurity, cloud infrastructure, and IT consulting services.',
          icon: '🔧',
          logo: 'tec.png',
          tags: ['Technology Solutions', 'IT Consulting', 'Infrastructure']
        },
        { 
          name: 'Antisyphon Training', 
          description: 'Cybersecurity training organization providing hands-on security education and professional development courses.',
          icon: '🎓',
          logo: 'antisyphon.png',
          tags: ['Training', 'Education', 'Professional Development']
        },
        { 
          name: 'Hospital Services Corporation', 
          description: 'HSC provides a wide variety of services to the healthcare industry and general business community, servicing customers in several different states.',
          icon: '🎓',
          logo: 'hsc.jpeg',
          tags: ['Health', 'Healthcare', 'Family']
        },
        { 
          name: 'LMNTRIX', 
          description: 'AI-driven Managed Extended Detection and Response (MXDR) services, combining threat hunting, deception, and real-time incident response through a unified platform.',
          icon: '🎓',
          logo: 'lmntrix.jpeg',
          tags: ['AI', 'MXDR', 'Threat Hunting']
        }
      ]
    },
    {
      tier: 'community',
      title: '🌟 Community Partners',
      sponsors: [
        { 
          name: 'New Mexico Technology Council', 
          description: 'State-wide organization supporting technology companies and professionals throughout New Mexico.',
          icon: '🏛️',
          logo: 'nmtc.png',
          tags: ['Technology Council', 'New Mexico', 'Professional Network']
        },
        { 
          name: 'City of Albuquerque', 
          description: 'Official municipal government supporting local technology initiatives and cybersecurity awareness programs.',
          icon: '🏛️',
          logo: 'cityofalburquerque.png',
          tags: ['Government', 'Municipal', 'Technology Support']
        },
        { 
          name: 'ISACA', 
          description: 'Global association of information systems professionals focused on governance, risk management, and cybersecurity.',
          icon: '📋',
          logo: 'isaca.png',
          tags: ['Professional Association', 'Governance', 'Risk Management']
        },
        { 
          name: 'New Mexico Cybersecurity Center of Excellence', 
          description: 'State initiative promoting cybersecurity education, research, and workforce development in New Mexico.',
          icon: '🎯',
          logo: 'newmexicotech.png',
          tags: ['Education', 'Research', 'Workforce Development']
        },
        { 
          name: 'Fidel Consulting Group', 
          description: 'Management consulting firm providing strategic advisory services and technology solutions to organizations.',
          icon: '💼',
          logo: 'fidel.png',
          tags: ['Consulting', 'Strategic Advisory', 'Technology Solutions']
        },
        { 
          name: 'InfraGard New Mexico', 
          description: 'FBI-sponsored partnership between law enforcement and private sector for cybersecurity information sharing.',
          icon: '🔍',
          logo: 'newmexicomembersalliance.png',
          tags: ['FBI Partnership', 'Information Sharing', 'Law Enforcement']
        },
        { 
          name: 'ChileSec', 
          description: 'Chilean cybersecurity community fostering international collaboration and knowledge sharing in information security.',
          icon: '🌶️',
          logo: 'chilie.png',
          tags: ['International', 'Community', 'Collaboration']
        }
      ]
    }
  ];

  const benefits = [
    {
      title: 'Brand Visibility',
      description: 'Prominent logo placement on website, conference materials, signage, and digital platforms reaching thousands of cybersecurity professionals.'
    },
    {
      title: 'Networking Opportunities',
      description: 'Direct access to cybersecurity leaders, decision makers, and emerging talent through structured networking sessions and social events.'
    },
    {
      title: 'Thought Leadership',
      description: 'Speaking opportunities, workshop slots, and panel discussions to showcase your expertise and establish your company as an industry leader.'
    },
    {
      title: 'Talent Acquisition',
      description: 'Connect with top cybersecurity professionals, students, and career changers looking for new opportunities in the field.'
    }
  ];

  const handleImageError = (sponsorName) => {
    setImageErrors(prev => ({
      ...prev,
      [sponsorName]: true
    }));
  };

  // Function to handle sponsorship package download
  const handleDownloadSponsorshipKit = () => {
    try {
      const link = document.createElement('a');
      link.href = '/document/BsidesABQsponsorshipkit.pdf';
      link.download = 'BSidesABQsponsorshipkit.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading sponsorship kit:', error);
      // Fallback: open in new tab
      window.open('/document/BsidesABQsponsorshipkit.pdf', '_blank');
    }
  };

  // Function to handle contact email
  const handleContactTeam = () => {
    const subject = encodeURIComponent('Security BSides Albuquerque 2025 - Sponsorship Inquiry');
    const body = encodeURIComponent(`Hello Security BSides Albuquerque Team,

I am interested in learning more about sponsorship opportunities for Security BSides Albuquerque 2025.

Please provide me with:
- Available sponsorship packages
- Pricing information
- Benefits and deliverables
- Timeline and deadlines

Company Name: [Your Company]
Contact Person: [Your Name]
Email: [Your Email]
Phone: [Your Phone]

Thank you for your time, and I look forward to hearing from you.

Best regards,
[Your Name]`);

    const emailAddress = 'sponsors@bsidesabq.org'; // Replace with actual email
    const mailtoLink = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    
    window.location.href = mailtoLink;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  return (
    <SponsorsContainer id="sponsors">
      <AnimatedBackground />
      <div className="container">
        <SponsorsHeader>
          <SponsorsTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Sponsors & Partners
          </SponsorsTitle>
          <SponsorsSubtitle>
            Thank you to our amazing sponsors who make Security BSides Albuquerque possible
          </SponsorsSubtitle>
        </SponsorsHeader>

        {sponsorTiers.map((tierData) => (
          <SponsorTier key={tierData.tier}>
            <TierHeader>
              <TierTitle tier={tierData.tier}>
                {tierData.title}
              </TierTitle>
            </TierHeader>
            
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <SponsorGrid tier={tierData.tier}>
                {tierData.sponsors.map((sponsor, index) => (
                  <SponsorCard
                    key={sponsor.name}
                    tier={tierData.tier}
                    variants={cardVariants}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SponsorContent>
                      <SponsorLogoContainer tier={tierData.tier}>
                        {sponsor.logo && !imageErrors[sponsor.name] ? (
                          <SponsorLogo
                            tier={tierData.tier}
                            src={`/sponsors/${sponsor.logo}`}
                            alt={`${sponsor.name} logo`}
                            onError={() => handleImageError(sponsor.name)}
                          />
                        ) : (
                          <SponsorFallbackLogo tier={tierData.tier}>
                            {sponsor.icon}
                          </SponsorFallbackLogo>
                        )}
                      </SponsorLogoContainer>
                      
                      <SponsorInfo>
                        <SponsorName tier={tierData.tier}>{sponsor.name}</SponsorName>
                        <SponsorDescription>{sponsor.description}</SponsorDescription>
                        <SponsorTags>
                          {sponsor.tags.map((tag, tagIndex) => (
                            <SponsorTag key={tagIndex} tier={tierData.tier}>{tag}</SponsorTag>
                          ))}
                        </SponsorTags>
                      </SponsorInfo>
                    </SponsorContent>
                  </SponsorCard>
                ))}
              </SponsorGrid>
            </motion.div>
          </SponsorTier>
        ))}

        <BecomeSponsorSection id="becomesponsor">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SponsorSectionTitle>
              Become a Sponsor
            </SponsorSectionTitle>
            <SponsorSectionDescription>
              Join us in supporting the cybersecurity community in New Mexico. 
              Multiple sponsorship packages available to fit your needs and budget, 
              from startup-friendly options to enterprise-level partnerships.
            </SponsorSectionDescription>

            <SponsorshipBenefits>
              {benefits.map((benefit, index) => (
                <BenefitCard
                  key={benefit.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.03 }}
                >
                  <h4>{benefit.title}</h4>
                  <p>{benefit.description}</p>
                </BenefitCard>
              ))}
            </SponsorshipBenefits>

            <CTAButtons>
              <Button 
                variant="primary" 
                size="large"
                onClick={handleDownloadSponsorshipKit}
              >
                📄 View Sponsorship Packages
              </Button>
              <Button 
                variant="secondary" 
                size="large"
                onClick={handleContactTeam}
              >
                📧 Contact Our Team
              </Button>
            </CTAButtons>
          </motion.div>
        </BecomeSponsorSection>
      </div>
    </SponsorsContainer>
  );
};

export default Sponsors;